import env from '@/config/env';

/**
 * Reads the tenant workspace out of the browser's address bar.
 *
 * One build serves every tenant from `*.minetech.rw`, so the hostname is the
 * only thing that tells the SPA which workspace it is. This must agree with the
 * backend's `TenantResolverService.extractSubdomain` and with the nginx regex
 * vhost — the same rules, expressed once per layer.
 */

/** RFC 1123 label: 3-63 chars, lowercase alphanumerics and hyphens, no edge hyphen. */
export const SUBDOMAIN_PATTERN = /^(?!-)[a-z0-9-]{3,63}(?<!-)$/;

/**
 * Labels that are platform hostnames, never tenants.
 *
 * Mirrors `RESERVED_SUBDOMAINS` in the backend validator. Kept here so the
 * provisioning form can reject a reserved name without a round-trip; the backend
 * remains the authority, and its list is the one that must not drift.
 */
export const RESERVED_SUBDOMAINS = new Set([
  'www', 'app', 'apex', 'public', 'private',
  'api', 'sandbox', 'docs', 'mobile', 'storage', 'staging', 'landing',
  'mail', 'smtp', 'webmail', 'pop', 'imap', 'ftp', 'autodiscover',
  'ns', 'ns1', 'ns2', 'dns',
  'grafana', 'monitoring', 'prometheus', 'alertmanager', 'alerts', 'uptime',
  'dozzle', 'logs', 'metrics', 'status',
  'core', 'gateway', 'payments', 'notifications', 'graphql', 'ws', 'socket',
  'billing',
  'admin', 'superadmin', 'root', 'system', 'internal', 'auth', 'login',
  'signup', 'register', 'dashboard', 'portal', 'tenant', 'tenants',
  'cdn', 'static', 'assets', 'media',
  'support', 'help',
  'demo', 'test', 'dev',
]);

export type SubdomainRejectionReason = 'format' | 'reserved';

export function checkSubdomain(
  value: string,
): { valid: true } | { valid: false; reason: SubdomainRejectionReason } {
  if (!SUBDOMAIN_PATTERN.test(value)) return { valid: false, reason: 'format' };
  if (RESERVED_SUBDOMAINS.has(value)) return { valid: false, reason: 'reserved' };
  return { valid: true };
}

/** Configured root domains, longest first so `staging.minetech.rw` wins over `minetech.rw`. */
function rootDomains(): string[] {
  return env.TENANT_ROOT_DOMAIN.split(',')
    .map((domain) => domain.trim().toLowerCase().replace(/^\.+|\.+$/g, ''))
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
}

/**
 * The tenant label for a hostname, or null when the host is not a tenant
 * address — the apex, a platform host, an IP, or a bare `localhost`.
 */
export function extractTenantSubdomain(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : '',
): string | null {
  const host = hostname.trim().toLowerCase().replace(/\.+$/, '');
  if (!host) return null;

  for (const root of rootDomains()) {
    if (host === root) return null; // the apex itself
    if (!host.endsWith(`.${root}`)) continue;

    const label = host.slice(0, -(root.length + 1));

    // A remaining dot means a deeper name than this root describes. Almost always
    // a misconfigured VITE_TENANT_ROOT_DOMAIN rather than a genuine non-tenant
    // host: with the root left at `minetech.rw`, every
    // `<tenant>.staging.minetech.rw` yields the label `<tenant>.staging`, lands
    // here, and returns null — so the boot gate concludes "not a tenant host" and
    // lets everything through. That failure is silent and looks like the gate
    // simply not working, so say so out loud.
    if (label.includes('.')) {
      console.warn(
        `[tenant-host] "${host}" sits below the configured root domain "${root}" ` +
          `by more than one label, so it cannot be read as a tenant address. ` +
          `VITE_TENANT_ROOT_DOMAIN is probably wrong for this environment ` +
          `(currently "${env.TENANT_ROOT_DOMAIN}").`,
      );
      return null;
    }

    return checkSubdomain(label).valid ? label : null;
  }

  return null;
}

/** True when the current page is a tenant workspace rather than the platform origin. */
export function isTenantHost(hostname?: string): boolean {
  return extractTenantSubdomain(hostname) !== null;
}

/** The canonical root domain, for suffix hints in the provisioning UI. */
export function canonicalRootDomain(): string {
  return rootDomains()[rootDomains().length - 1] ?? 'minetech.rw';
}

/** Full workspace URL for a subdomain, e.g. `https://rutongo.minetech.rw`. */
export function buildWorkspaceUrl(subdomain: string): string {
  const root = canonicalRootDomain();
  const scheme = root.startsWith('localhost') ? 'http' : 'https';
  const port =
    typeof window !== 'undefined' && window.location.port && root.startsWith('localhost')
      ? `:${window.location.port}`
      : '';
  return `${scheme}://${subdomain}.${root}${port}`;
}
